/**
 * Functions to trim various geometric types w/ cutting geometry, often planes.
 * @module
 */
import RBush from "rbush";
import type { AABB3 } from "./aabb3Types";
import type { BRep, Face, FaceTopo, SolidSide } from "./brep";
import { brepNew, faceAABB3, faceNew, faceTopoNew } from "./brep";
import { closestPointPolyLine2, type ClosestPointToPolyLine2 } from "./closestPoint";
import {
  classifyContainmentLoopLoopFast,
  classifyContainmentVec2Polygon,
  classifyContainmentVec2PolyLine2,
  PolyLine2Containment,
  Vec2PolyLine2ContainmentClass,
} from "./containment";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import type { FacePlaneIntersection, FacePlaneIntersectionSegment, PolyLine2PolyLine2Intersection } from "./intersect";
import {
  facePlaneIntersectionSegmentCopy,
  intersectFacePlane,
  intersectFacePlaneFromBRep,
  intersectPolyLine2PolyLine2,
} from "./intersect";
import { CurveCurvePointIntersection, CurveCurveRangeIntersection } from "./intersectLine2Line2";
import { IntersectionKind } from "./intersectRay2";
import { planeCopy, planeEval, planeNormal, PlaneSide, vec3PlaneSide } from "./plane";
import type { Plane } from "./planeTypes";
import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2Extend,
  polyLine2IsClosed,
  polyLine2Length,
  polyLine2MergeCoincidentVertices,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2Vertex,
} from "./polyLine2";
import type { PolyLine2MultiPolygon } from "./polyLine2Types";
import { polyLine2New, type PolyLine2 } from "./polyLine2Types";
import { hashInts2, range } from "./utils";
import { vec2Dist, vec2DistSquared } from "./vec2";
import { vec3ToString } from "./vec3";

export function trimBRepByPlane(brep: BRep, cutPlane: Plane, errs: string[]): BRep | null {
  const inputFaces = brep.faces;
  const [normX, normY, normZ] = planeNormal(cutPlane);
  const { originX, originY, originZ } = cutPlane;

  const res = trimBRepByPlaneInternalStage2(brep, cutPlane, errs);
  if (!res) {
    return null;
  }

  const { faces, faceTopos, facePlaneIntersectionResults } = res;

  for (let i = 0, l = inputFaces.length; i < l; i++) {
    const face = inputFaces[i];
    const intResult = facePlaneIntersectionResults[i];

    if (!intResult || intResult.kind !== IntersectionKind.TRANSVERSE) {
      if (polyLine2BelowPlane(face.loops[0], face.plane, originX, originY, originZ, normX, normY, normZ)) {
        faces.push(face);
        faceTopos.push(faceTopoNew());
      }

      continue;
    }

    const resultLoops: PolyLine2[] = [];

    faceSplitWithFacePlaneIntersectionSegments(face, cutPlane, intResult.segments, resultLoops);

    if (resultLoops.length === 0) {
      continue;
    }

    faces.push(faceNew(resultLoops, planeCopy(face.plane)));
    faceTopos.push(faceTopoNew());
  }

  if (errs.length > 0) {
    return null;
  }

  return brepNew(faces, faceTopos);
}

export function trimFaceByPlane(face: Face, cutPlane: Plane, errs: string[]): Face {
  const e = intersectFacePlane(face, cutPlane, errs);

  if (!e || e.kind === IntersectionKind.TANGENT) {
    return face;
  }

  const finalLoops: PolyLine2[] = [];
  faceSplitWithFacePlaneIntersectionSegments(face, cutPlane, e.segments, finalLoops);

  if (finalLoops.length === 0) {
    errs.push("No loops found after splitting face.");
    return face;
  }

  return faceNew(finalLoops, face.plane);
}

function trimBRepByPlaneInternalStage2(brep: BRep, cutPlane: Plane, errs: string[]) {
  const stage1Result = trimBRepByPlaneInternalStage1(brep, cutPlane, errs);
  if (errs.length > 0 || !stage1Result) {
    return null;
  }

  const { loops, loopPolyLine2s, facePlaneIntersectionResults } = stage1Result;

  const faceTopos: FaceTopo[] = [];
  const faces: Face[] = [];
  buildSplitFaces(loopPolyLine2s, [], cutPlane, range(loops.length), faces, faceTopos, [], errs);
  if (errs.length > 0) {
    return null;
  }

  return {
    faces,
    faceTopos,
    facePlaneIntersectionResults,
  };
}

export function intersectBRepPlane(brep: BRep, cutPlane: Plane, errs: string[]) {
  const r = trimBRepByPlaneInternalStage2(brep, cutPlane, errs);
  if (errs.length > 0 || !r) {
    return null;
  }
  return {
    faces: r.faces,
  };
}

export function trimBRepByPlaneInternalStage0(brep: BRep, cutPlane: Plane, errs: string[]) {
  // extract some useful constants
  const faces = brep.faces;
  const [normX, normY, normZ] = planeNormal(cutPlane);
  const { originX, originY, originZ } = cutPlane;

  // intersect all brep faces with the plane, yielding an unstructured list of segments

  const facePlaneIntersectionResults = new Array<FacePlaneIntersection>(brep.faces.length);
  const segments = new Array<FacePlaneIntersectionSegment>();

  for (let i = 0, l = faces.length; i < l; i++) {
    const face = faces[i];

    if (!aabb3DoesIntersectPlane(faceAABB3(face), originX, originY, originZ, normX, normY, normZ)) {
      continue;
    }

    const intResult = intersectFacePlaneFromBRep(brep, i, cutPlane, errs);
    if (intResult) {
      facePlaneIntersectionResults[i] = intResult;

      if (intResult.kind === IntersectionKind.TRANSVERSE && intResult.segments) {
        for (const int of intResult.segments) {
          segments.push(int);
        }
      }
    }
  }

  return { facePlaneIntersectionResults, segments };
}

function trimBRepByPlaneInternalStage1(brep: BRep, cutPlane: Plane, errs: string[]) {
  // intersect all brep faces with the plane, yielding an unstructured list of segments

  const { facePlaneIntersectionResults, segments } = trimBRepByPlaneInternalStage0(brep, cutPlane, errs);

  // put all of the segment ends (expressed in 2d coordinate space in the cut plane) into an rtree

  interface RBox {
    x: number;
    y: number;
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    segmentIndex: number;
  }

  const rbushItems: RBox[] = [];
  for (let i = 0, l = segments.length; i < l; i++) {
    const segment = segments[i];

    rbushItems.push({
      minX: segment.planeU0 - DIST_EPSILON,
      minY: segment.planeV0 - DIST_EPSILON,
      maxX: segment.planeU0 + DIST_EPSILON,
      maxY: segment.planeV0 + DIST_EPSILON,
      x: segment.planeU0,
      y: segment.planeV0,
      segmentIndex: i,
    });

    rbushItems.push({
      minX: segment.planeU1 - DIST_EPSILON,
      minY: segment.planeV1 - DIST_EPSILON,
      maxX: segment.planeU1 + DIST_EPSILON,
      maxY: segment.planeV1 + DIST_EPSILON,
      x: segment.planeU1,
      y: segment.planeV1,
      segmentIndex: i,
    });

    if (!(vec2Dist(segment.planeU1, segment.planeV1, segment.planeU0, segment.planeV0) > DIST_EPSILON)) {
      console.assert(false, "invalid segment length");
    }
  }

  const rtree = new RBush<RBox>();
  rtree.load(rbushItems);

  // build the intersection loops

  const segmentVisited = new Array<boolean>(segments.length).fill(false);
  const loops: FacePlaneIntersectionSegment[][] = []; // loops are stored here
  // const searchBox = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  for (let i = 0, l = segments.length; i < l; i++) {
    if (segmentVisited[i]) {
      continue;
    }

    const loop: FacePlaneIntersectionSegment[] = [];
    loops.push(loop);

    let currentSegIndex = i;

    while (true) {
      const currentSeg = segments[currentSegIndex];
      loop.push(currentSeg);

      segmentVisited[currentSegIndex] = true;

      const searchBox: any = {};

      const x = currentSeg.planeU1;
      const y = currentSeg.planeV1;

      // make box around end point
      searchBox.minX = x - DIST_EPSILON;
      searchBox.minY = y - DIST_EPSILON;

      searchBox.maxX = x + DIST_EPSILON;
      searchBox.maxY = y + DIST_EPSILON;

      // const res = rtree.search(searchBox);

      const res: RBox[] = [];

      for (let j = 0, lj = rbushItems.length; j < lj; j++) {
        const item = rbushItems[j];
        if (vec2Dist(item.x, item.y, x, y) < 1.5 * DIST_EPSILON) {
          res.push(item);
        }
      }

      if (res.length === 2) {
        const segIndex0 = res[0].segmentIndex;
        const segIndex1 = res[1].segmentIndex;

        // cases:
        if (segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
          // both pts have been visited - close loop
          break;
        } else if (segmentVisited[segIndex0] && !segmentVisited[segIndex1]) {
          // segment end 1 is visited - extend loop with that segment
          currentSegIndex = res[1].segmentIndex;
        } else if (!segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
          // segment end 0 is visited - extend loop with that segment
          currentSegIndex = segIndex0;
        } else {
          // illegal case
          errs.push("I could not close one of the loops.");
          return null;
        }

        const newSeg = segments[currentSegIndex];

        if (vec2Dist(currentSeg.planeU1, currentSeg.planeV1, newSeg.planeU0, newSeg.planeV0) > DIST_EPSILON) {
          facePlaneIntersectionSegmentReverse(newSeg);
        }
      } else {
        const [x, y, z] = planeEval(cutPlane, currentSeg.planeU1, currentSeg.planeV1);

        errs.push(
          `I could not close one of the intersection loops. There are ${
            res.length
          } intersection segments intersecting at ${vec3ToString(x, y, z)}, but there should be 2.`,
        );
        return null;
      }
    }
  }

  // construct the cleaved face, the loops define it, the cutPlane is the new face's plane

  const loopPolyLine2s: PolyLine2[] = loops.map((x) => {
    const pl = polyLine2New(x.length * 2 + 2);

    for (let i = 0, l = x.length; i < l; i++) {
      const seg = x[i];
      pl[2 * i] = seg.planeU0;
      pl[2 * i + 1] = seg.planeV0;
    }

    // close loop
    pl[pl.length - 2] = x[0].planeU0;
    pl[pl.length - 1] = x[0].planeV0;

    return pl;
  });

  return {
    loopPolyLine2s,
    loops,
    facePlaneIntersectionResults,
  };
}

function facePlaneIntersectionSegmentReverse(seg: FacePlaneIntersectionSegment): FacePlaneIntersectionSegment {
  let t = seg.planeU0;
  seg.planeU0 = seg.planeU1;
  seg.planeU1 = t;

  t = seg.planeV0;
  seg.planeV0 = seg.planeV1;
  seg.planeV1 = t;

  t = seg.faceU0;
  seg.faceU0 = seg.faceU1;
  seg.faceU1 = t;

  t = seg.faceV0;
  seg.faceV0 = seg.faceV1;
  seg.faceV1 = t;

  t = seg.loop0;
  seg.loop0 = seg.loop1;
  seg.loop1 = t;

  t = seg.loopEdgeIndex0;
  seg.loopEdgeIndex0 = seg.loopEdgeIndex1;
  seg.loopEdgeIndex1 = t;

  return seg;
}

function aabb3DoesIntersectPlane(
  b: AABB3,
  x: number,
  y: number,
  z: number,
  normX: number,
  normY: number,
  normZ: number,
): boolean {
  // intersection is the case where the bb is on one consistent
  // side of the plane

  const { minX, minY, minZ, maxX, maxY, maxZ } = b;

  const minSide = vec3PlaneSide(minX, minY, minZ, x, y, z, normX, normY, normZ);

  if (minSide === PlaneSide.COPLANAR) {
    return true;
  }

  return (
    vec3PlaneSide(maxX, minY, minZ, x, y, z, normX, normY, normZ) !== minSide ||
    vec3PlaneSide(maxX, maxY, minZ, x, y, z, normX, normY, normZ) !== minSide ||
    vec3PlaneSide(maxX, minY, maxZ, x, y, z, normX, normY, normZ) !== minSide ||
    vec3PlaneSide(maxX, maxY, maxZ, x, y, z, normX, normY, normZ) !== minSide ||
    vec3PlaneSide(minX, maxY, minZ, x, y, z, normX, normY, normZ) !== minSide ||
    vec3PlaneSide(minX, maxY, maxZ, x, y, z, normX, normY, normZ) !== minSide ||
    vec3PlaneSide(minX, minY, maxZ, x, y, z, normX, normY, normZ) !== minSide
  );
}

export function faceSplitWithFacePlaneIntersectionSegments(
  face: Face,
  cutPlane: Plane,
  segments: FacePlaneIntersectionSegment[],
  finalLoopsBelow: PolyLine2[],
  finalLoopsAbove?: PolyLine2[],
) {
  // two "half edges" for each cut segment
  const loopIsCut: { [loopIndex: number]: boolean } = {};
  const cutEdges: { [hash: number]: FacePlaneIntersectionSegment } = {};
  for (const seg of segments) {
    loopIsCut[seg.loop0] = loopIsCut[seg.loop0] = true;
    cutEdges[hashInts2(seg.loop0, seg.loopEdgeIndex0)] = seg;
    cutEdges[hashInts2(seg.loop1, seg.loopEdgeIndex1)] = facePlaneIntersectionSegmentReverse(
      facePlaneIntersectionSegmentCopy(seg),
    );
  }

  const loops = face.loops;
  const newLoops: number[][] = [];
  const loopEdgesVisited = {};

  for (let i = 0; i < loops.length; i++) {
    if (loopIsCut[i]) {
      loopEdgesVisited[i] = new Array<boolean>(loops[i].length / 2);
    }
  }

  for (let i = 0; i < loops.length; i++) {
    if (!loopIsCut[i]) {
      continue;
    }

    for (let j = 0; j < loops[i].length; j += 2) {
      if (loopEdgesVisited[i][j / 2]) {
        continue;
      } // we've processed this vertex

      // start new loop
      const newLoop: number[] = [];
      newLoops.push(newLoop);

      // so we know when we've closed the loop
      const startLoopIdx = i;
      const startVertIdx = j;

      // loop state
      let curLoopIdx = startLoopIdx;
      let curLoop = loops[startLoopIdx];
      let curVertIdx = startVertIdx;

      do {
        if (loopEdgesVisited[curLoopIdx]) {
          loopEdgesVisited[curLoopIdx][curVertIdx / 2] = 1;
        }
        newLoop.push(curLoop[curVertIdx], curLoop[curVertIdx + 1]);

        const cutEdge = cutEdges[hashInts2(curLoopIdx, curVertIdx / 2)];

        if (cutEdge) {
          newLoop.push(cutEdge.faceU0, cutEdge.faceV0);
          newLoop.push(cutEdge.faceU1, cutEdge.faceV1);

          curLoopIdx = cutEdge.loop1;
          curLoop = loops[curLoopIdx];
          curVertIdx = (cutEdge.loopEdgeIndex1 * 2 + 2) % curLoop.length;
        } else {
          curVertIdx = (curVertIdx + 2) % curLoop.length;
        }
      } while (!(curLoopIdx === startLoopIdx && curVertIdx === startVertIdx));

      // ensure loop is closed
      if (
        vec2DistSquared(newLoop[0], newLoop[1], newLoop[newLoop.length - 2], newLoop[newLoop.length - 1]) >
        DIST_EPSILON_SQUARED
      ) {
        newLoop.push(newLoop[0], newLoop[1]);
      }
    }
  }

  const [normX, normY, normZ] = planeNormal(cutPlane);
  const { originX, originY, originZ } = cutPlane;

  for (let i = 0, l = newLoops.length; i < l; i++) {
    const loop = newLoops[i];
    const l = polyLine2New(loop);
    if (polyLine2BelowPlane(l, face.plane, originX, originY, originZ, normX, normY, normZ)) {
      finalLoopsBelow.push(l);
    }
  }

  for (let i = 0, l = loops.length; i < l; i++) {
    if (loopIsCut[i]) {
      continue;
    }

    const loop = loops[i];

    if (polyLine2BelowPlane(loop, face.plane, originX, originY, originZ, normX, normY, normZ)) {
      finalLoopsBelow.push(polyLine2New(loop));
    } else if (finalLoopsAbove) {
      finalLoopsAbove.push(polyLine2New(loop));
    }
  }
}

function findOuterLoops(loops: PolyLine2[], loopIndices: number[]): number[] {
  const res: number[] = [];

  for (let i = 0, l = loopIndices.length; i < l; i++) {
    let isOuter = true;

    for (let j = 0; j < l; j++) {
      if (i === j) {
        continue;
      }

      if (classifyContainmentLoopLoopFast(loops, loopIndices[i], loopIndices[j]) !== PolyLine2Containment.OUTSIDE) {
        isOuter = false;
        break;
      }
    }

    if (isOuter) {
      res.push(loopIndices[i]);
    }
  }

  // this will occur if the loops are coincident
  if (loopIndices.length > 0 && res.length === 0) {
    res.push(loopIndices[0]);
  }

  return res;
}

function findInnerLoops(loops: PolyLine2[], loopIndices: number[], outerLoopIndex: number): number[] {
  const res: number[] = [];
  for (let j = 0, l = loopIndices.length; j < l; j++) {
    if (loopIndices[j] === outerLoopIndex) {
      continue;
    }

    if (classifyContainmentLoopLoopFast(loops, loopIndices[j], outerLoopIndex) !== PolyLine2Containment.OUTSIDE) {
      res.push(j);
    }
  }
  return res;
}

function subtractNumArray(a: number[], b: number[]) {
  const m: { [v: number]: boolean } = {};
  for (const ib of b) {
    m[ib] = true;
  }

  const res: number[] = [];
  for (const ia of a) {
    if (!m[ia]) {
      res.push(ia);
    }
  }

  return res;
}

export function buildSplitFaces(
  loops: PolyLine2[],
  loopSides: SolidSide[],
  cutPlane: Plane,
  loopIndices: number[],
  faces: Face[],
  faceTopos: FaceTopo[],
  faceSides: SolidSide[],
  errs: string[],
) {
  if (loopIndices.length === 0) {
    return undefined;
  }

  // find outer loops
  const outerLoopIndices = findOuterLoops(loops, loopIndices);
  if (outerLoopIndices.length === 0) {
    return undefined;
  }

  for (const outerLoopIndex of outerLoopIndices) {
    // find all of the loops that are inside of the loop
    const innerLoopIndices = findInnerLoops(loops, loopIndices, outerLoopIndex);

    // if it has less than 2 inner loops, take this shortcut
    if (innerLoopIndices.length < 2) {
      faces.push(faceNew([loops[outerLoopIndex]].concat(innerLoopIndices.map((x) => loops[x])), planeCopy(cutPlane)));
      faceTopos.push(faceTopoNew());

      if (loopSides) {
        faceSides.push(loopSides[outerLoopIndex]);
      }
      continue;
    }

    // otherwise, there may be nested inner loops

    // find the loops inside of this outerLoop that have no inner loops, these are the inner loops
    // of this outer loop
    const innerOuterLoopIndices = findOuterLoops(loops, innerLoopIndices);

    // set correct orientation
    polyLine2SetCCW(loops[outerLoopIndex]);
    innerOuterLoopIndices.map((i) => polyLine2SetCW(loops[i]));

    // create the new face
    faces.push(faceNew([loops[outerLoopIndex]].concat(innerLoopIndices.map((x) => loops[x])), planeCopy(cutPlane)));
    faceTopos.push(faceTopoNew());

    if (loopSides) {
      faceSides.push(loopSides[outerLoopIndex]);
    }

    // subtract the innerOuterLoopIndices from innerLoopIndices and recurse
    const innerInnerLoops = subtractNumArray(innerLoopIndices, innerOuterLoopIndices);

    // recurse on Acc
    buildSplitFaces(loops, loopSides, cutPlane, innerInnerLoops, faces, faceTopos, faceSides, errs);
    if (errs.length > 0) {
      return null;
    }
  }
  return undefined;
}

function polyLine2BelowPlane(loop: PolyLine2, loopPlane: Plane, originX, originY, originZ, normX, normY, normZ) {
  let below = true;

  for (let j = 0; j < loop.length; j += 2) {
    const u = loop[j];
    const v = loop[j + 1];

    const [x, y, z] = planeEval(loopPlane, u, v);

    if (vec3PlaneSide(x, y, z, originX, originY, originZ, normX, normY, normZ) === PlaneSide.FRONT) {
      below = false;
      break;
    }
  }

  return below;
}

export function splitPolyLineAtPoints(pl: PolyLine2, pts: ClosestPointToPolyLine2[]) {
  if (pts.length === 0) {
    return polyLine2Copy(pl);
  }

  // sort by edge index and break ties with parameter
  pts.sort((a, b) => (a.edgeIndex !== b.edgeIndex ? a.edgeIndex - b.edgeIndex : a.t - b.t));

  const pl2 = [pl[0], pl[1]];

  let splitPtCursor = 0;

  for (let i = 0; i < pl.length - 2; i += 2) {
    const bx = pl[i + 2];
    const by = pl[i + 3];

    let lastT = 0;

    for (let lj = pts.length; splitPtCursor < lj; splitPtCursor++) {
      const pt = pts[splitPtCursor];
      if (i / 2 !== pt.edgeIndex) {
        break;
      }

      if (Math.abs(pt.t - lastT) <= DIST_EPSILON) {
        continue;
      }

      pl2.push(pt.x, pt.y);
      lastT = pt.t;
    }

    pl2.push(bx, by);
  }

  return polyLine2New(pl2);
}

/**
 * Trims a 2d polyline using a collection of trimming polylines.
 *
 * This algorithm procedes by:
 *
 * - Intersecting the subject with the trimming paths, yielding a collection of intersection points.
 * - Splitting the subject at the intersection points while preserving the start and end intersection points.
 * - Of those split parts, find the one closest to the selection point.
 * - Re-split the subject using only the start and end intersection points of the closest component.
 * - Return the closest component as the removed component, and the rest as the remaining components.
 *
 * Special cases:
 *
 * - The start and end intersection points may be the end points of the path
 * - Closed paths where the start/end segment are closest will have both the start and end split components
 */
export function trimPolyLine2(
  subject: PolyLine2,
  trimmingPaths: PolyLine2[],
  selectionPointX: number,
  selectionPointY: number,
) {
  // find all intersections
  const allInts: PolyLine2PolyLine2Intersection[] = [];

  for (let trimPath of trimmingPaths) {
    // extend unclosed trim paths very slightly to ensure such paths act as trims when
    // the shapes are just touching
    if (!polyLine2IsClosed(trimPath)) {
      trimPath = polyLine2Extend(polyLine2Copy(trimPath), 1e-3, 1e-3, true, true);
    }

    const ints = intersectPolyLine2PolyLine2(subject, trimPath, DIST_EPSILON);
    allInts.push(...ints);
  }

  // convert the intersections to a simpler, normalized data type

  const splitPoints: PolyLine2SplitPoint[] = [];

  for (const int of allInts) {
    if (int.int instanceof CurveCurvePointIntersection) {
      splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.param0 });
    } else if (int.int instanceof CurveCurveRangeIntersection) {
      // range intersections are handle by simply introducing two split points
      splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.startParam0 });
      splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.endParam0 });
    }
  }

  const endPoints = [
    { edgeIndex: 0, t: 0 },
    {
      edgeIndex: polyLine2EdgeCount(subject) - 1,
      t: polyLine2EdgeLength(subject, polyLine2EdgeCount(subject) - 1),
    },
  ];

  splitPoints.push(...endPoints);

  // split the subject

  const comps = splitPolyLine2AtSplitPoints(subject, splitPoints);

  // find the closest component from the subject after splitting

  const { closest: closestComp } = closestPolyLine2SplitComponent(comps, selectionPointX, selectionPointY);
  if (!closestComp) {
    return {
      removed: [],
      rest: [...comps.map((x) => x.component)],
    };
  }

  const finalSplitPoints = [...endPoints, closestComp.startSplitPoint, closestComp.endSplitPoint];

  // isolate the closest component

  const finalComps = splitPolyLine2AtSplitPoints(subject, finalSplitPoints);
  const finalClosest = closestPolyLine2SplitComponent(finalComps, selectionPointX, selectionPointY);
  if (!finalClosest.closest) {
    return {
      removed: [],
      rest: [...finalComps.map((x) => x.component)]
        .map(polyLine2MergeCoincidentVertices)
        .filter((x) => polyLine2Length(x) > DIST_EPSILON),
    };
  }

  return {
    removed: [finalClosest.closest.component]
      .map(polyLine2MergeCoincidentVertices)
      .filter((x) => polyLine2Length(x) > DIST_EPSILON),
    rest: finalClosest.rest
      .map((x) => x.component)
      .map(polyLine2MergeCoincidentVertices)
      .filter((x) => polyLine2Length(x) > DIST_EPSILON),
  };
}

/**
 * Trims a polyline using a closed polyline loop.
 * It returns the sub-rails that are inside the loop and the sub-rails that are outside the loop.
 */
export function trimRailWithClosedPolyLine(
  rail: PolyLine2,
  loop: PolyLine2,
  insideRails: PolyLine2[] = [],
  outsideRails: PolyLine2[] = [],
  tol: number = DIST_EPSILON,
  dotTol: number = 0,
) {
  // in the loop is not closed, we simply return the rail
  if (!polyLine2IsClosed(loop)) {
    insideRails.push(polyLine2Copy(rail));

    return {
      insideRails,
      outsideRails,
    };
  }

  // detect all the intersections between the rail and the loop
  const ints = intersectPolyLine2PolyLine2(rail, loop, tol, dotTol);

  // convert the intersections split points
  const splitPoints: PolyLine2SplitPoint[] = [];

  for (const int of ints) {
    if (int.int instanceof CurveCurvePointIntersection) {
      splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.param0 });
    } else if (int.int instanceof CurveCurveRangeIntersection) {
      // range intersections are handle by simply introducing two split points
      splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.startParam0 });
      splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.endParam0 });
    }
  }

  // add the end points to the split points
  const endPoints = [
    { edgeIndex: 0, t: 0 },
    {
      edgeIndex: polyLine2EdgeCount(rail) - 1,
      t: polyLine2EdgeLength(rail, polyLine2EdgeCount(rail) - 1),
    },
  ];

  splitPoints.push(...endPoints);

  // split the input rail
  const splitComponents = splitPolyLine2AtSplitPoints(rail, splitPoints);

  // classify the split components
  for (const { component } of splitComponents) {
    const simplifiedComponent = polyLine2MergeCoincidentVertices(component);

    // we check the middle point of the first segment to see if it's inside or outside the loop
    // we cannot use the first point because it might be right at the border of the loop
    const [x, y] = polyLine2EdgeEval(simplifiedComponent, 0, 0.5, true);
    if (classifyContainmentVec2PolyLine2(x, y, loop, tol, dotTol) === Vec2PolyLine2ContainmentClass.OUTSIDE) {
      outsideRails.push(simplifiedComponent);
    } else {
      insideRails.push(simplifiedComponent);
    }
  }

  return {
    insideRails,
    outsideRails,
  };
}

function closestPolyLine2SplitComponent(comps: PolyLine2SplitComponent[], x: number, y: number) {
  let closestComp: PolyLine2SplitComponent | undefined = undefined;
  let minDist = Infinity;

  for (const comp of comps) {
    const pt = closestPointPolyLine2(comp.component, x, y);

    if (pt.dist < minDist) {
      closestComp = comp;
      minDist = pt.dist;
    }
  }

  return {
    closest: closestComp,
    rest: comps.filter((x) => x !== closestComp),
  };
}

interface PolyLine2SplitComponent {
  startSplitPoint: PolyLine2SplitPoint;
  endSplitPoint: PolyLine2SplitPoint;
  component: PolyLine2;
}

interface PolyLine2SplitPoint {
  edgeIndex: number;
  t: number; // non-normalized
}

export function splitPolyLine2AtSplitPoints(
  subject: PolyLine2,
  splitPoints: PolyLine2SplitPoint[],
): PolyLine2SplitComponent[] {
  // sort
  splitPoints.sort((a, b) => (a.edgeIndex !== b.edgeIndex ? a.edgeIndex - b.edgeIndex : a.t - b.t));

  // split the subject at the split points into parts
  const splitComponents: PolyLine2SplitComponent[] = [];

  // the start split point is stored here, we update this on every iteration
  let startSplitPoint = splitPoints[0];

  for (let i = 1; i < splitPoints.length; i++) {
    const endSplitPoint = splitPoints[i];

    // skip duplicates
    if (
      endSplitPoint.edgeIndex === startSplitPoint.edgeIndex &&
      Math.abs(endSplitPoint.t - startSplitPoint.t) < DIST_EPSILON
    ) {
      startSplitPoint = endSplitPoint;
      continue;
    }

    const [startX, startY] = polyLine2EdgeEval(subject, startSplitPoint.edgeIndex, startSplitPoint.t, false);

    const component: number[] = [startX, startY];

    if (startSplitPoint.edgeIndex < endSplitPoint.edgeIndex) {
      for (let i = startSplitPoint.edgeIndex; i < endSplitPoint.edgeIndex; i++) {
        const [x, y] = polyLine2Vertex(subject, i + 1);
        component.push(x, y);
      }
    }

    const [endX, endY] = polyLine2EdgeEval(subject, endSplitPoint.edgeIndex, endSplitPoint.t, false);
    component.push(endX, endY);

    const pl = polyLine2New(component);

    if (pl.length > 0) {
      splitComponents.push({ startSplitPoint: startSplitPoint, endSplitPoint: endSplitPoint, component: pl });
    }

    startSplitPoint = endSplitPoint;
  }

  return splitComponents;
}

export interface ClassifiedPolyLine2Segment {
  readonly component: PolyLine2;
  /** Indices into the regions array. Empty if the segment is not contained in any region. */
  readonly regionIndices: number[];
}

/**
 * Splits a polyline by multiple closed polygon regions and classifies each
 * resulting sub-segment by which regions contain it.
 *
 * Each region is a `PolyLine2[]` where index 0 is the outer boundary and
 * subsequent entries are hole loops. A point is considered inside the region
 * only if it is inside the outer boundary and not strictly inside any hole.
 */
export function splitPolyLine2ByRegions(
  pl: PolyLine2,
  regions: PolyLine2MultiPolygon,
  tol: number = DIST_EPSILON,
): ClassifiedPolyLine2Segment[] {
  const splitPoints: PolyLine2SplitPoint[] = [];

  for (const region of regions) {
    for (const loop of region) {
      if (!polyLine2IsClosed(loop)) {
        continue;
      }

      const ints = intersectPolyLine2PolyLine2(pl, loop, tol);
      for (const int of ints) {
        if (int.int instanceof CurveCurvePointIntersection) {
          splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.param0 });
        } else if (int.int instanceof CurveCurveRangeIntersection) {
          splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.startParam0 });
          splitPoints.push({ edgeIndex: int.edgeIndexA, t: int.int.endParam0 });
        }
      }
    }
  }

  splitPoints.push(
    { edgeIndex: 0, t: 0 },
    {
      edgeIndex: polyLine2EdgeCount(pl) - 1,
      t: polyLine2EdgeLength(pl, polyLine2EdgeCount(pl) - 1),
    },
  );

  const components = splitPolyLine2AtSplitPoints(pl, splitPoints);

  const results: ClassifiedPolyLine2Segment[] = [];
  for (const { component } of components) {
    const simplified = polyLine2MergeCoincidentVertices(component);
    const [x, y] = polyLine2EdgeEval(simplified, 0, 0.5, true);

    const regionIndices: number[] = [];
    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      const containment = classifyContainmentVec2Polygon(x, y, region, tol);

      if (
        containment === Vec2PolyLine2ContainmentClass.INSIDE ||
        containment === Vec2PolyLine2ContainmentClass.COINCIDENT
      ) {
        regionIndices.push(i);
      }
    }

    results.push({ component: simplified, regionIndices });
  }

  return results;
}
